import { z } from 'zod';
import {
  FinanceFlowSchema,
  FinancialLineSourceSchema,
  FinancialLineStatusSchema,
  TariffBasisSchema,
} from '../enums/finance.js';
import { IsoDateStringSchema } from './common.js';

/**
 * Contrats de `/finance/*`. **Liste blanche** : chaque réponse a son propre
 * schéma, jamais un objet lead enrichi. Un champ absent d'ici ne sort pas — en
 * particulier, la vue réseau (`MyNetwork*`) ne porte NI CA, NI marge, NI nom
 * d'acheteur, NI prix de vente.
 */

/**
 * Identifiant d'entité référencée (partner, verticale, acheteur). Les ids
 * historiques ne sont pas tous des UUID v7 (seed), d'où un garde-fou de forme
 * plutôt qu'un `uuid()` strict.
 */
const RefIdSchema = z.string().min(1).max(64);

/**
 * Montant décimal à 2 décimales max, sérialisé en chaîne (`"87.00"`) pour ne
 * jamais transiter par un flottant. Un nombre est accepté en entrée et
 * converti.
 */
export const MoneyStringSchema = z.preprocess(
  (v) => (typeof v === 'number' ? v.toFixed(2) : v),
  z.string().regex(/^-?\d{1,8}(\.\d{1,2})?$/, 'Montant invalide (ex. 87.00)'),
);

/** Flux qu'un admin peut tarifer ou corriger dans ce lot (pas la commission vendeur, lot 5). */
export const PricedFinanceFlowSchema = z.enum(['revenue', 'payout']);

// ── Grille (tariffs) ───────────────────────────────────────────────────

/** Note obligatoire sur toute modification de la grille (création, remplacement, clôture). */
export const TariffNoteSchema = z
  .string({ required_error: 'Note obligatoire' })
  .trim()
  .min(3, 'Note obligatoire (3 caractères minimum)')
  .max(1000);

/** Montant de grille : positif ou nul, 2 décimales max. */
const TariffAmountSchema = MoneyStringSchema.refine((v) => !v.startsWith('-'), 'Montant négatif interdit');

/**
 * Création d'une version de grille. Une ligne n'est JAMAIS modifiée : on la
 * ferme (`POST /finance/tariffs/:id/close`) puis on en insère une nouvelle.
 *
 *  - `revenue` : `deliveryPartnerId` = l'acheteur (nul = toute la verticale),
 *    `partnerId` interdit ;
 *  - `payout`  : `partnerId` = le réseau (nul = ligne par défaut),
 *    `deliveryPartnerId` interdit.
 */
export const CreateTariffSchema = z
  .object({
    flow: PricedFinanceFlowSchema,
    deliveryPartnerId: RefIdSchema.nullable().optional(),
    partnerId: RefIdSchema.nullable().optional(),
    verticaleId: RefIdSchema,
    /** Offre (nom d'offre telco, type d'énergie…). Nul = toutes offres. */
    offerKey: z.string().trim().min(1).max(200).nullable().optional(),
    basis: TariffBasisSchema,
    amount: TariffAmountSchema,
    validFrom: IsoDateStringSchema,
    validTo: IsoDateStringSchema.nullable().optional(),
    /** Obligatoire à la saisie (cf. `superRefine`) ; optionnel dans le type pour les écritures internes. */
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    if (v.flow === 'revenue' && !v.deliveryPartnerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deliveryPartnerId'],
        message: 'Une grille de CA exige un acheteur',
      });
    }
    if (!v.note || v.note.length < 3) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['note'],
        message: 'Note obligatoire (3 caractères minimum)',
      });
    }
    if (v.flow === 'revenue' && v.partnerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['partnerId'],
        message: 'Une grille de CA se rattache à un acheteur, pas à un réseau',
      });
    }
    if (v.flow === 'payout' && v.deliveryPartnerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['deliveryPartnerId'],
        message: 'Une grille de rétrocession se rattache à un réseau, pas à un acheteur',
      });
    }
    if (v.validTo && new Date(v.validTo) <= new Date(v.validFrom)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['validTo'],
        message: 'La fin de validité doit suivre le début',
      });
    }
  });
export type CreateTariffInput = z.infer<typeof CreateTariffSchema>;

export const CloseTariffSchema = z.object({
  /** Fin de validité (exclue). */
  validTo: IsoDateStringSchema,
  /** Motif de la clôture — journalisé (`finance_access_logs`). */
  note: TariffNoteSchema,
});
export type CloseTariffInput = z.infer<typeof CloseTariffSchema>;

/**
 * « Modifier » une ligne de grille = la fermer à `validFrom` ET créer la
 * version suivante à partir de cette même date, en UNE transaction : jamais
 * de trou ni de chevauchement. Le périmètre (flux, verticale, acheteur ou
 * réseau, offre) est hérité de la ligne remplacée — il ne se change pas ici.
 */
export const ReplaceTariffSchema = z.object({
  /** Début de la nouvelle version = fin (exclue) de la version remplacée. */
  validFrom: IsoDateStringSchema,
  amount: TariffAmountSchema,
  basis: TariffBasisSchema,
  note: TariffNoteSchema,
});
export type ReplaceTariffInput = z.infer<typeof ReplaceTariffSchema>;

export const TariffListQuerySchema = z.object({
  flow: FinanceFlowSchema.optional(),
  verticaleId: RefIdSchema.optional(),
  partnerId: RefIdSchema.optional(),
  deliveryPartnerId: RefIdSchema.optional(),
  /** Ne garder que les lignes valides à cet instant. */
  activeAt: IsoDateStringSchema.optional(),
});
export type TariffListQuery = z.infer<typeof TariffListQuerySchema>;

export const TariffSchema = z.object({
  id: z.string(),
  flow: FinanceFlowSchema,
  deliveryPartnerId: z.string().nullable(),
  partnerId: z.string().nullable(),
  verticaleId: z.string(),
  offerKey: z.string().nullable(),
  basis: TariffBasisSchema,
  amount: z.string(),
  validFrom: IsoDateStringSchema,
  validTo: IsoDateStringSchema.nullable(),
  createdByUserId: z.string(),
  /** Nom de l'auteur (liste admin uniquement). */
  createdByName: z.string().nullable().optional(),
  createdAt: IsoDateStringSchema,
  note: z.string().nullable(),
});
export type Tariff = z.infer<typeof TariffSchema>;

export const ReplaceTariffResultSchema = z.object({
  closed: TariffSchema,
  created: TariffSchema,
});
export type ReplaceTariffResult = z.infer<typeof ReplaceTariffResultSchema>;

/** Résultat de `POST /finance/tariffs/seed` : ce qui a été créé, et pourquoi le reste ne l'a pas été. */
export const TariffSeedResultSchema = z.object({
  created: z.array(TariffSchema),
  skipped: z.array(
    z.object({
      flow: PricedFinanceFlowSchema,
      verticaleId: z.string(),
      deliveryPartnerId: z.string().nullable(),
      reason: z.enum(['already_set', 'zero_amount']),
    }),
  ),
});
export type TariffSeedResult = z.infer<typeof TariffSeedResultSchema>;

// ── Grand livre (admin) ───────────────────────────────────────────────

export const FinancialLineSchema = z.object({
  id: z.string(),
  leadId: z.string(),
  flow: FinanceFlowSchema,
  amount: z.string(),
  status: FinancialLineStatusSchema,
  source: FinancialLineSourceSchema,
  tariffId: z.string().nullable(),
  tmkSaleId: z.string().nullable(),
  reason: z.string(),
  createdByUserId: z.string().nullable(),
  createdAt: IsoDateStringSchema,
});
export type FinancialLine = z.infer<typeof FinancialLineSchema>;

/**
 * Correction manuelle (admin) : une ligne signée de plus, motif obligatoire.
 * Le moteur ne la compense jamais.
 */
export const ManualFinancialLineSchema = z.object({
  flow: PricedFinanceFlowSchema,
  amount: MoneyStringSchema.refine((v) => Number(v) !== 0, 'Montant nul'),
  status: z.enum(['estimated', 'earned']).default('earned'),
  reason: z.string().trim().min(5, 'Motif obligatoire').max(1000),
});
export type ManualFinancialLineInput = z.infer<typeof ManualFinancialLineSchema>;

// ── Synthèse (admin) ──────────────────────────────────────────────────

export const FINANCE_SUMMARY_GROUP_BY = ['month', 'partner', 'verticale', 'deliveryPartner'] as const;
export const FinanceSummaryGroupBySchema = z.enum(FINANCE_SUMMARY_GROUP_BY);

/**
 * Période sur la date de CRÉATION du lead — la même que celle qui choisit le
 * tarif. Filtres facultatifs (combinés en ET) : servent au dépliage d'une
 * ligne du tableau de bord (un réseau → ses verticales, etc.).
 */
export const FinanceSummaryQuerySchema = z.object({
  from: IsoDateStringSchema,
  to: IsoDateStringSchema,
  groupBy: FinanceSummaryGroupBySchema.default('month'),
  partnerId: RefIdSchema.optional(),
  verticaleId: RefIdSchema.optional(),
  deliveryPartnerId: RefIdSchema.optional(),
});
export type FinanceSummaryQuery = z.infer<typeof FinanceSummaryQuerySchema>;

export const FinanceSummaryRowSchema = z.object({
  /** Mois `YYYY-MM` (Europe/Paris) ou id de l'entité. */
  key: z.string(),
  label: z.string(),
  revenueEarned: z.string(),
  payoutEarned: z.string(),
  /** CA acquis − rétrocession acquise. */
  margin: z.string(),
  revenueEstimated: z.string(),
  payoutEstimated: z.string(),
  /** Fiches ayant au moins une ligne au grand livre sur la période. */
  leadCount: z.number().int(),
  /**
   * Fiches transmises à l'acheteur (livrées ou au-delà,
   * `DELIVERED_OR_LATER_STATUSES`, non archivées), créées sur la période —
   * qu'elles aient ou non une ligne au grand livre. Dénominateur de la
   * « valeur d'une fiche » (CA acquis ÷ fiches transmises).
   */
  transmittedLeads: z.number().int(),
  /** Ventes activées (`COMMISSIONABLE_SALE_STATUSES`), fiches non archivées. */
  activatedSales: z.number().int(),
  /** Ventes validées, pas encore activées (pipeline, `ESTIMATED_SALE_STATUSES`). */
  pendingSales: z.number().int(),
});
export type FinanceSummaryRow = z.infer<typeof FinanceSummaryRowSchema>;

export const FinanceSummarySchema = z.object({
  groupBy: FinanceSummaryGroupBySchema,
  rows: z.array(FinanceSummaryRowSchema),
  total: FinanceSummaryRowSchema,
});
export type FinanceSummary = z.infer<typeof FinanceSummarySchema>;

// ── Vue réseau (responsable partenaire) ───────────────────────────────

export const MyNetworkQuerySchema = z.object({
  from: IsoDateStringSchema,
  to: IsoDateStringSchema,
});
export type MyNetworkQuery = z.infer<typeof MyNetworkQuerySchema>;

export const MyNetworkLinesQuerySchema = MyNetworkQuerySchema.extend({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type MyNetworkLinesQuery = z.infer<typeof MyNetworkLinesQuerySchema>;

/** Synthèse réseau : rétrocession uniquement, par mois. */
export const MyNetworkSummaryRowSchema = z.object({
  month: z.string(),
  payoutEarned: z.string(),
  payoutEstimated: z.string(),
  leadCount: z.number().int(),
});
export const MyNetworkSummarySchema = z.object({
  rows: z.array(MyNetworkSummaryRowSchema),
  total: MyNetworkSummaryRowSchema,
});
export type MyNetworkSummary = z.infer<typeof MyNetworkSummarySchema>;

/** Ligne de rétrocession vue par le réseau. Rien sur l'acheteur ni le CA. */
export const MyNetworkLineSchema = z.object({
  id: z.string(),
  leadId: z.string(),
  leadCreatedAt: IsoDateStringSchema,
  partnerId: z.string(),
  partnerName: z.string(),
  verticaleName: z.string().nullable(),
  /**
   * Matricule du vendeur de la fiche — jamais son nom. Le vendeur est
   * forcément du réseau (le périmètre est celui du RP).
   */
  vendorMatricule: z.string().nullable(),
  amount: z.string(),
  status: FinancialLineStatusSchema,
  reason: z.string(),
  createdAt: IsoDateStringSchema,
});
export type MyNetworkLine = z.infer<typeof MyNetworkLineSchema>;

export const MyNetworkLinesSchema = z.object({
  items: z.array(MyNetworkLineSchema),
  total: z.number().int(),
  page: z.number().int(),
  pageSize: z.number().int(),
});
export type MyNetworkLines = z.infer<typeof MyNetworkLinesSchema>;

// ── Appel à facturation (responsable partenaire) ─────────────────────

export const MY_NETWORK_EXPORT_FORMATS = ['xlsx', 'csv'] as const;

/**
 * `GET /finance/my-network/export` : la rétrocession d'UN mois civil
 * (Europe/Paris, date de création du lead — la même période que la synthèse
 * réseau et la liste des lignes), une ligne par fiche.
 */
export const MyNetworkExportQuerySchema = z.object({
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Mois attendu au format AAAA-MM'),
  format: z.enum(MY_NETWORK_EXPORT_FORMATS).default('xlsx'),
});
export type MyNetworkExportQuery = z.infer<typeof MyNetworkExportQuerySchema>;

// ── Mise en route (admin) ─────────────────────────────────────────────

/**
 * `GET /finance/setup-status` : de quoi afficher l'état de la mise en route
 * (Configuration › Rémunération › Mise en route) et le bandeau « grand livre
 * vide » du tableau de bord Finance. Des comptes, aucun montant.
 */
export const FinanceSetupStatusSchema = z.object({
  tariffs: z.object({
    /** Lignes de grille `per_sale` (initialisation depuis les verticales). */
    perSale: z.number().int(),
    /** Lignes de grille `per_lead` (reprise de la grille historique). */
    perLead: z.number().int(),
  }),
  ledger: z.object({
    lines: z.number().int(),
    leads: z.number().int(),
    lastLineAt: IsoDateStringSchema.nullable(),
  }),
  flags: z.object({
    financeLedger: z.boolean(),
    financeVisibility: z.enum(['admin_only', 'everyone']),
  }),
  /** Dernier passage de chaque étape de reprise (journal `finance_access_logs`). */
  lastRuns: z.array(
    z.object({
      action: z.string(),
      at: IsoDateStringSchema,
      /** Cible journalisée (run, simulation ou écriture, compteurs). */
      detail: z.string().nullable(),
    }),
  ),
});
export type FinanceSetupStatus = z.infer<typeof FinanceSetupStatusSchema>;
